import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography, DatePicker, Select, Button, Table, Space, message, Card, Row, Col, Spin, Input, Modal, Descriptions, Divider, Tag, Tabs, Form, Input as AntInput, Grid, Pagination
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { SearchOutlined, ReloadOutlined, PhoneOutlined, PlusOutlined, BookOutlined, FileExcelOutlined, BarChartOutlined, HistoryOutlined } from '@ant-design/icons';
import {
  cskhService,
  CskhBenhNhanDto,
  CskhDanhMucDto,
  CskhDmCachLienHeDto,
  CskhDmLoaiDanhGiaDto,
  CskhDmTrangThaiDto,
  CskhPhieuDto,
  CskhPhieuSummaryDto,
  CskhDichVuDto,
  CskhDichVuSuDungDto,
  CskhReportDto,
} from '../services/cskhService';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = AntInput;

const CskhTab: React.FC = () => {
  // Responsive breakpoints (AntD: xs<576, sm≥576, md≥768, lg≥992, xl≥1200)
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md; // <768px: render card list thay table
  const isLgUp = !!screens.lg;   // ≥992px: 4 cột filter / hàng

  // Tab chính
  const [mainActiveTab, setMainActiveTab] = useState<string>('cskh');

  // Bộ lọc
  const [tuNgay, setTuNgay] = useState<Dayjs>(dayjs());
  const [denNgay, setDenNgay] = useState<Dayjs>(dayjs());
  const [maDangDieuTri, setMaDangDieuTri] = useState<number>(0);
  const [maDoiTuong, setMaDoiTuong] = useState<number>(0);
  const [maKK, setMaKK] = useState<number>(0);
  const [maphong, setMaphong] = useState<number>(0);
  const [nameSearch, setNameSearch] = useState('');

  // Dữ liệu báo cáo
  const [reportData, setReportData] = useState<CskhReportDto[]>([]);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportTuNgay, setReportTuNgay] = useState<Dayjs>(dayjs().startOf('month'));
  const [reportDenNgay, setReportDenNgay] = useState<Dayjs>(dayjs());
  const [reportMaTT, setReportMaTT] = useState<number>(0);

  // Danh mục HIS (lọc)
  const [dmDangDieuTri, setDmDangDieuTri] = useState<CskhDanhMucDto[]>([]);
  const [dmDoiTuong, setDmDoiTuong] = useState<CskhDanhMucDto[]>([]);
  const [dmKhoaKham, setDmKhoaKham] = useState<CskhDanhMucDto[]>([]);
  const [dmPhong, setDmPhong] = useState<CskhDanhMucDto[]>([]);
  const [loadingPhong, setLoadingPhong] = useState(false);

  // Danh mục phiếu CSKH
  const [dmCachLienHe, setDmCachLienHe] = useState<CskhDmCachLienHeDto[]>([]);
  const [dmLoaiDanhGia, setDmLoaiDanhGia] = useState<CskhDmLoaiDanhGiaDto[]>([]);
  const [dmTrangThai, setDmTrangThai] = useState<CskhDmTrangThaiDto[]>([]);

  // Dữ liệu & loading
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<CskhBenhNhanDto[]>([]);

  // Pagination dùng chung cho Table (desktop) & Card list (mobile)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  // Summary số phiếu + lần gọi gần nhất (key: maBN)
  const [summaryMap, setSummaryMap] = useState<Map<string, CskhPhieuSummaryDto>>(new Map());
  const [loadingSummary, setLoadingSummary] = useState(false);

  // Modal chi tiết bệnh nhân
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailData, setDetailData] = useState<CskhBenhNhanDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Tab Lịch sử CSKH & Dịch vụ
  const [activeTab, setActiveTab] = useState<string>('info');
  const [danhSachPhieu, setDanhSachPhieu] = useState<CskhPhieuDto[]>([]);
  const [loadingPhieu, setLoadingPhieu] = useState(false);
  const [dsDichVuSuDung, setDsDichVuSuDung] = useState<CskhDichVuSuDungDto[]>([]);
  const [loadingDichVuSuDung, setLoadingDichVuSuDung] = useState(false);
  // Modal Tạo phiếu
  const [taoPhieuModalOpen, setTaoPhieuModalOpen] = useState(false);
  const [taoPhieuLoading, setTaoPhieuLoading] = useState(false);
  const [taoPhieuForm] = Form.useForm();
  const selectedMaTT = Form.useWatch('maTT', taoPhieuForm);

  // Modal Danh mục dịch vụ
  const [dichVuModalOpen, setDichVuModalOpen] = useState(false);
  const [dichVuLoading, setDichVuLoading] = useState(false);
  const [dichVuData, setDichVuData] = useState<CskhDichVuDto[]>([]);
  const [dichVuSearch, setDichVuSearch] = useState('');

  const handleOpenDichVu = async () => {
    setDichVuModalOpen(true);
    await handleSearchDichVu('');
  };

  const handleSearchDichVu = async (search: string) => {
    setDichVuLoading(true);
    try {
      const res = await cskhService.getDmDichVu(search);
      setDichVuData(res);
    } catch (error) {
      console.error('Lỗi tải danh mục dịch vụ:', error);
      message.error('Không thể tải danh mục dịch vụ');
    } finally {
      setDichVuLoading(false);
    }
  };

  const handleOpenDetail = async (maKCB: string) => {
    setDetailModalOpen(true);
    setActiveTab('info');
    setDetailData(null);
    setDanhSachPhieu([]);
    setDsDichVuSuDung([]);
    setDetailLoading(true);
    try {
      const res = await cskhService.getBenhNhanChiTiet(maKCB);
      setDetailData(res);
      if (!res) message.warning('Không tìm thấy thông tin chi tiết');
    } catch (error: any) {
      console.error('Lỗi tải chi tiết bệnh nhân:', error);
      message.error(error.response?.data?.message || 'Lỗi tải thông tin chi tiết');
    } finally {
      setDetailLoading(false);
    }
  };

  // Load dữ liệu lazy khi user click tab tương ứng
  const handleTabChange = async (key: string) => {
    setActiveTab(key);
    if (key === 'phieu' && detailData?.maBN) {
      await loadDanhSachPhieu(detailData.maBN);
    }
    if (key === 'dichvu' && detailData?.maKCB) {
      await loadDichVuSuDung(detailData.maKCB);
    }
  };
  const loadDanhSachPhieu = async (maBN: string) => {
    setLoadingPhieu(true);
    try {
      const list = await cskhService.getDanhSachPhieu(maBN);
      setDanhSachPhieu(list);
    } catch (error: any) {
      console.error('Lỗi tải danh sách phiếu:', error);
      message.error(error.response?.data?.message || 'Lỗi tải danh sách phiếu');
    } finally {
      setLoadingPhieu(false);
    }
  };
  const loadDichVuSuDung = async (maKCB: string) => {
    setLoadingDichVuSuDung(true);
    try {
      const list = await cskhService.getDichVuByMaKCB(maKCB);
      setDsDichVuSuDung(list);
    } catch (error: any) {
      console.error('Lỗi tải danh sách dịch vụ đã sử dụng:', error);
      message.error(error.response?.data?.message || 'Lỗi tải danh sách dịch vụ');
    } finally {
      setLoadingDichVuSuDung(false);
    }
  };

  // Load summary (số phiếu, lần gọi gần nhất) khi data list thay đổi
  useEffect(() => {
    if (!data || data.length === 0) {
      setSummaryMap(new Map());
      return;
    }
    const maBNList = Array.from(new Set(data.map(bn => bn.maBN).filter(Boolean) as string[]));
    if (maBNList.length === 0) return;

    setLoadingSummary(true);
    cskhService.getSummaryPhieu(maBNList)
      .then(summaries => {
        const map = new Map<string, CskhPhieuSummaryDto>();
        summaries.forEach(s => map.set(s.maBN, s));
        setSummaryMap(map);
      })
      .catch(error => console.error('Lỗi tải summary phiếu CSKH:', error))
      .finally(() => setLoadingSummary(false));
  }, [data]);

  // Mở form tạo phiếu: reset + điền defaults
  const handleMoTaoPhieu = () => {
    if (!detailData) return;
    taoPhieuForm.resetFields();
    // maTT = 0 (chưa chọn) → khóa maLDG về 4 (Không có TT) theo yêu cầu
    taoPhieuForm.setFieldsValue({
      maCLH: 0,
      maTT: 0,
      maLDG: 4,
      ngayGoi: dayjs(),
      ghiChu: ''
    });
    setTaoPhieuModalOpen(true);
  };

  const handleSubmitTaoPhieu = async () => {
    if (!detailData) return;
    try {
      const values = await taoPhieuForm.validateFields();
      setTaoPhieuLoading(true);
      // Logic cứng an toàn: khi maTT != 4 (hoặc chưa chọn) -> luôn ép maLDG = 4;
      //                      khi maTT == 4 -> giữ nguyên theo lựa chọn (hoặc null nếu chưa chọn)
      const maLdgFinal = values.maTT !== 4 ? 4 : (values.maLDG ?? null);
      await cskhService.createPhieu({
        maKCB: detailData.maKCB,
        maBN: detailData.maBN,
        maCLH: values.maCLH,
        maTT: values.maTT,
        maLDG: maLdgFinal,
        ngayGoi: values.ngayGoi.format('YYYY-MM-DDTHH:mm:ss'),
        ghiChu: values.ghiChu || null
      });
      message.success('Tạo phiếu CSKH thành công');
      setTaoPhieuModalOpen(false);
      // Refresh UI: summary map + danh sách phiếu (nếu đang ở tab đó)
      if (detailData?.maBN) {
        await Promise.all([
          refreshSingleSummary(detailData.maBN),
          activeTab === 'phieu' ? loadDanhSachPhieu(detailData.maBN) : Promise.resolve()
        ]);
      }
    } catch (error: any) {
      if (error?.errorFields) return; // Validate lỗi => bỏ qua
      console.error('Lỗi tạo phiếu:', error);
      message.error(error.response?.data?.message || 'Tạo phiếu không thành công');
    } finally {
      setTaoPhieuLoading(false);
    }
  };

  // Refresh summary 1 maBN (sau khi tạo phiếu mới)
  const refreshSingleSummary = async (maBN: string) => {
    try {
      const list = await cskhService.getSummaryPhieu([maBN]);
      setSummaryMap(prev => {
        const next = new Map(prev);
        list.forEach(s => next.set(s.maBN, s));
        return next;
      });
    } catch (e) { /* ignore */ }
  };

  // Lần đầu load tất cả danh mục + dữ liệu mặc định (hôm nay)
  useEffect(() => {
    loadAllDanhMuc();
    setTimeout(() => handleSearch(), 200);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAllDanhMuc = async () => {
    try {
      const [ddt, dt, kk, clh, ldg, tt] = await Promise.all([
        cskhService.getDangDieuTri(),
        cskhService.getDoiTuong(),
        cskhService.getKhoaKham(),
        cskhService.getDmCachLienHe(),
        cskhService.getDmLoaiDanhGia(),
        cskhService.getDmTrangThai(),
      ]);
      setDmDangDieuTri(ddt);
      setDmDoiTuong(dt);
      setDmKhoaKham(kk);
      setDmCachLienHe(clh);
      setDmLoaiDanhGia(ldg);
      setDmTrangThai(tt);
      await loadDanhMucPhong(0);
    } catch (error) {
      console.error('Lỗi tải danh mục CSKH:', error);
      message.error('Không thể tải danh mục lọc.');
    }
  };

  // Cascaded dropdown: đổi Khoa -> load lại phòng theo khoa, reset phòng về 0 (Tất cả)
  useEffect(() => {
    loadDanhMucPhong(maKK);
    setMaphong(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maKK]);

  const loadDanhMucPhong = async (khoaId: number) => {
    setLoadingPhong(true);
    try {
      const phongList = await cskhService.getPhongByKhoa(khoaId);
      setDmPhong(phongList);
    } catch (error) {
      console.error('Lỗi tải danh sách phòng:', error);
      setDmPhong([]);
    } finally {
      setLoadingPhong(false);
    }
  };

  const handleSearch = async () => {
    // Validate
    if (!tuNgay || !denNgay) {
      message.warning('Vui lòng chọn đầy đủ Từ ngày và Đến ngày');
      return;
    }
    if (tuNgay.isAfter(denNgay)) {
      message.error('Từ ngày không được lớn hơn Đến ngày');
      return;
    }

    setLoading(true);
    try {
      const result = await cskhService.getBenhNhan({
        tuNgay,
        denNgay,
        maDangDieuTri,
        maDoiTuong,
        maKK,
        maphong,
        searchTerm: nameSearch.trim()
      });
      setData(result);
      message.success(`Tìm thấy ${result.length} bệnh nhân`);
    } catch (error: any) {
      console.error('Lỗi tải danh sách bệnh nhân CSKH:', error);
      message.error(error.response?.data?.message || 'Lỗi tải danh sách bệnh nhân');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setTuNgay(dayjs());
    setDenNgay(dayjs());
    setMaDangDieuTri(0);
    setMaDoiTuong(0);
    setMaKK(0);
    setMaphong(0);
    setNameSearch('');
  };

  const handleSearchReport = async () => {
    setReportLoading(true);
    try {
      const res = await cskhService.getReport({
        tuNgay: reportTuNgay,
        denNgay: reportDenNgay,
        maTT: reportMaTT
      });
      setReportData(res);
      message.success(`Tìm thấy ${res.length} bản ghi báo cáo`);
    } catch (error) {
      console.error('Lỗi tải báo cáo:', error);
      message.error('Không thể tải dữ liệu báo cáo');
    } finally {
      setReportLoading(false);
    }
  };

  const handleExportExcel = () => {
    if (reportData.length === 0) {
      message.warning('Không có dữ liệu để xuất Excel');
      return;
    }

    let html = `
      <style>
        table { border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; }
        th, td { border: 1px solid #000; padding: 8px; text-align: left; }
        th { background-color: #f2f2f2; font-weight: bold; }
      </style>
      <table>
        <thead>
          <tr>
            <th colspan="11" style="text-align: center; font-size: 18px;">BÁO CÁO NHẬT KÝ CHĂM SÓC KHÁCH HÀNG</th>
          </tr>
          <tr>
            <th colspan="11" style="text-align: center;">Thời gian: ${reportTuNgay.format('DD/MM/YYYY')} - ${reportDenNgay.format('DD/MM/YYYY')}</th>
          </tr>
          <tr>
            <th>STT</th>
            <th>Mã KCB</th>
            <th>Mã BN</th>
            <th>Họ tên</th>
            <th>SĐT</th>
            <th>Khoa</th>
            <th>Ngày gọi</th>
            <th>Cách LH</th>
            <th>Trạng thái</th>
            <th>Đánh giá</th>
            <th>Ghi chú</th>
            <th>NV thực hiện</th>
          </tr>
        </thead>
        <tbody>
    `;

    reportData.forEach((row, index) => {
      html += `
        <tr>
          <td>${index + 1}</td>
          <td>${row.maKCB}</td>
          <td>${row.maBN}</td>
          <td>${row.hoTen}</td>
          <td>${row.dienThoai || ''}</td>
          <td>${row.tenKK || ''}</td>
          <td>${dayjs(row.ngayGoi).format('HH:mm DD/MM/YYYY')}</td>
          <td>${row.tenCLH || ''}</td>
          <td>${row.tenTT || ''}</td>
          <td>${row.tenLoaiDG || ''}</td>
          <td>${row.ghiChu || ''}</td>
          <td>${row.tenNV || row.maNV}</td>
        </tr>
      `;
    });

    html += '</tbody></table>';

    const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `BaoCaoCSKH_${dayjs().format('YYYYMMDD_HHmm')}.xls`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const columns: ColumnsType<CskhBenhNhanDto> = [
    {
      title: 'STT',
      key: 'stt',
      width: 55,
      fixed: 'left',
      render: (_text, _record, index) => index + 1
    },
    {
      title: 'Mã KCB',
      dataIndex: 'maKCB',
      key: 'maKCB',
      width: 100,
      fixed: 'left',
      render: (v, record) => (
        <a onClick={() => handleOpenDetail(record.maKCB)} style={{ fontWeight: 600 }}>
          {v}
        </a>
      )
    },
    {
      title: 'Mã BN',
      dataIndex: 'maBN',
      key: 'maBN',
      width: 85
    },
    {
      title: 'Họ tên',
      dataIndex: 'hoTen',
      key: 'hoTen',
      width: 160,
      ellipsis: true,
      render: (v) => <Text strong>{v}</Text>
    },
    {
      title: 'Tuổi',
      dataIndex: 'tuoi',
      key: 'tuoi',
      width: 60,
      align: 'center'
    },
    {
      title: 'Giới tính',
      dataIndex: 'tenPhai',
      key: 'tenPhai',
      width: 75,
      align: 'center'
    },
    {
      title: 'Địa chỉ',
      dataIndex: 'diaChi',
      key: 'diaChi',
      width: 160,
      ellipsis: true,
      render: (v) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>{v}</Text>
      )
    },
    {
      title: 'Ngày ĐK',
      dataIndex: 'ngayDK',
      key: 'ngayDK',
      width: 120,
      render: (v) => v ? dayjs(v).format('DD/MM/YYYY HH:mm') : ''
    },
    {
      title: 'SĐT',
      dataIndex: 'dienThoai',
      key: 'dienThoai',
      width: 110,
      render: (v) => <Text copyable={!!v}>{v || '—'}</Text>
    },
    {
      title: 'Liên hệ',
      key: 'lienhe',
      width: 150,
      align: 'center',
      fixed: 'right',
      render: (_v, record) => {
        const s = record.maBN ? summaryMap.get(record.maBN) : undefined;
        if (loadingSummary) return <Spin size="small" />;
        if (!s || s.soPhieu === 0)
          return <Tag icon={<PhoneOutlined />} color="default">Chưa gọi</Tag>;
        const time = s.ngayGoiGanNhat ? dayjs(s.ngayGoiGanNhat).format('HH:mm DD/MM') : '';
        return (
          <Space direction="vertical" size={0} style={{ width: '100%' }}>
            <Tag icon={<PhoneOutlined />} color="green">
              Đã gọi ({s.soPhieu})
            </Tag>
            <Text type="secondary" style={{ fontSize: 11 }}>{time}</Text>
          </Space>
        );
      }
    }
  ];

  const renderCskhTab = () => (
    <>
      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title={
          <Space>
            <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
              🏥 Chăm sóc khách hàng
            </Title>
            <Divider type="vertical" />
            <Text type="secondary">Quản lý và nhật ký liên hệ bệnh nhân</Text>
          </Space>
        }
      >
        <Form layout="vertical">
          <Row gutter={[16, 0]}>
            {/* Hàng 1: Bộ lọc thời gian và đối tượng */}
            <Col xs={24} sm={12} lg={6}>
              <Form.Item label={<Text strong>Từ ngày</Text>}>
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  value={tuNgay}
                  onChange={(v) => setTuNgay(v as Dayjs)}
                  disabledDate={(current) => !!current && current.isAfter(dayjs())}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item label={<Text strong>Đến ngày</Text>}>
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  value={denNgay}
                  onChange={(v) => setDenNgay(v as Dayjs)}
                  disabledDate={(current) =>
                    !!current && (current.isBefore(tuNgay.startOf('day')) || current.isAfter(dayjs()))
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item label={<Text strong>Đang điều trị</Text>}>
                <Select
                  style={{ width: '100%' }}
                  value={maDangDieuTri}
                  onChange={setMaDangDieuTri}
                  showSearch
                  optionFilterProp="children"
                >
                  <Option value={0}>— Tất cả —</Option>
                  {dmDangDieuTri.map(item => (
                    <Option key={item.ma} value={item.ma}>{item.ten}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item label={<Text strong>Đối tượng</Text>}>
                <Select
                  style={{ width: '100%' }}
                  value={maDoiTuong}
                  onChange={setMaDoiTuong}
                  showSearch
                  optionFilterProp="children"
                >
                  <Option value={0}>— Tất cả —</Option>
                  {dmDoiTuong.map(item => (
                    <Option key={item.ma} value={item.ma}>{item.ten}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            {/* Hàng 2: Vị trí và Tìm kiếm nhanh */}
            <Col xs={24} sm={12} lg={6}>
              <Form.Item 
                label={<Text strong>Khoa khám</Text>}
              >
                <Select
                  style={{ width: '100%' }}
                  value={maKK}
                  onChange={setMaKK}
                  showSearch
                  optionFilterProp="children"
                >
                  <Option value={0}>— Tất cả khoa —</Option>
                  {dmKhoaKham.map(item => (
                    <Option key={item.ma} value={item.ma}>{item.ten}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Form.Item 
                label={<Text strong>Phòng {maKK === 0 && <Text type="warning" style={{ fontSize: 11 }}>(Chọn khoa trước)</Text>}</Text>}
              >
                <Select
                  style={{ width: '100%' }}
                  value={maphong}
                  onChange={setMaphong}
                  showSearch
                  optionFilterProp="children"
                  disabled={maKK === 0}
                  loading={loadingPhong}
                >
                  <Option value={0}>— Tất cả phòng —</Option>
                  {dmPhong.map(item => (
                    <Option key={item.ma} value={item.ma}>{item.ten}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} lg={12}>
              <Form.Item label={<Text strong>Tìm nhanh bệnh nhân (Tên / Mã BN / Mã KCB)</Text>}>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Input
                    style={{ flex: '1 1 200px', minWidth: 180 }}
                    prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                    placeholder="Nhập họ tên, mã bệnh nhân hoặc mã khám..."
                    value={nameSearch}
                    onChange={(e) => setNameSearch(e.target.value)}
                    onPressEnter={handleSearch}
                    allowClear
                  />
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={handleSearch}
                    loading={loading}
                    style={{ minWidth: 80 }}
                  >
                    Tìm
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleReset}
                    disabled={loading}
                  >
                    Đặt lại
                  </Button>
                  <Button
                    icon={<BookOutlined />}
                    onClick={handleOpenDichVu}
                    style={{ backgroundColor: '#52c41a', color: '#fff', border: 'none' }}
                  >
                    DM dịch vụ
                  </Button>
                </div>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* Bảng kết quả */}
      <Card
        size="small"
        title={
          <Space>
            <Text strong>Danh sách bệnh nhân</Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              (Tổng: {data.length} — click Mã KCB để xem chi tiết)
            </Text>
          </Space>
        }
      >
        <Spin spinning={loading}>
          {/* Desktop / Tablet → Table */}
          {!isMobile && (
            <Table<CskhBenhNhanDto>
              rowKey="maKCB"
              size="middle"
              columns={columns}
              dataSource={data}
              scroll={{ x: 1100 }}
              pagination={{
                current: page,
                pageSize,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ['10', '25', '50', '100'],
                showTotal: (total) => `Tổng: ${total} bệnh nhân`,
                onChange: (p, ps) => { setPage(p); setPageSize(ps); }
              }}
            />
          )}

          {/* Mobile → Card list thay Table */}
          {isMobile && (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {data.slice((page - 1) * pageSize, page * pageSize).map((bn, idx) => {
                  const s = bn.maBN ? summaryMap.get(bn.maBN) : undefined;
                  return (
                    <Card
                      key={bn.maKCB}
                      size="small"
                      onClick={() => handleOpenDetail(bn.maKCB)}
                      hoverable
                      styles={{ body: { padding: 12, cursor: 'pointer' } }}
                    >
                      <Space direction="vertical" size={6} style={{ width: '100%' }}>
                        <Row justify="space-between" align="top" wrap={false}>
                          <Col flex="auto" style={{ minWidth: 0, paddingRight: 8 }}>
                            <Text strong style={{ fontSize: 15 }}>{bn.hoTen}</Text>
                          </Col>
                          <Col flex="none">
                            {loadingSummary
                              ? <Spin size="small" />
                              : (!s || s.soPhieu === 0)
                                ? <Tag icon={<PhoneOutlined />} color="default">Chưa gọi</Tag>
                                : <Tag icon={<PhoneOutlined />} color="green">Đã gọi ({s.soPhieu})</Tag>}
                          </Col>
                        </Row>

                        <Row gutter={8} style={{ fontSize: 12 }}>
                          <Col span={12}>Mã KCB: <Text strong copyable>{bn.maKCB}</Text></Col>
                          <Col span={12}>Mã BN: {bn.maBN}</Col>
                          <Col span={12}>Tuổi/Giới: {bn.tuoi ?? '—'} / {bn.tenPhai ?? '—'}</Col>
                          <Col span={12}>Ngày ĐK: {bn.ngayDK ? dayjs(bn.ngayDK).format('DD/MM HH:mm') : '—'}</Col>
                          <Col span={24}>📞 SĐT: <Text copyable={!!bn.dienThoai}>{bn.dienThoai || '—'}</Text></Col>
                          <Col span={24} style={{ marginTop: 2 }}>
                            📍 <Text
                              type="secondary"
                              style={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                verticalAlign: 'top'
                              }}
                            >{bn.diaChi || '—'}</Text>
                          </Col>
                        </Row>

                        {s?.ngayGoiGanNhat && (
                          <Text type="secondary" style={{ fontSize: 11 }}>Gần nhất: {dayjs(s.ngayGoiGanNhat).format('HH:mm DD/MM/YYYY')}</Text>
                        )}
                      </Space>
                    </Card>
                  );
                })}
                {data.length === 0 && !loading && (
                  <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>Không có dữ liệu</div>
                )}
              </div>
              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <Pagination
                  current={page}
                  pageSize={pageSize}
                  total={data.length}
                  showSizeChanger
                  showQuickJumper
                  pageSizeOptions={['10', '25', '50', '100']}
                  showTotal={(total: number) => `Tổng: ${total} bệnh nhân`}
                  onChange={(p, ps) => { setPage(p); setPageSize(ps); }}
                />
              </div>
            </>
          )}
        </Spin>
      </Card>
    </>
  );

  const renderReportTab = () => (
    <Card size="small">
      <Form layout="inline" style={{ marginBottom: 16 }}>
        <Form.Item label={<Text strong>Từ ngày</Text>}>
          <DatePicker 
            format="DD/MM/YYYY" 
            value={reportTuNgay} 
            onChange={v => setReportTuNgay(v as Dayjs)} 
          />
        </Form.Item>
        <Form.Item label={<Text strong>Đến ngày</Text>}>
          <DatePicker 
            format="DD/MM/YYYY" 
            value={reportDenNgay} 
            onChange={v => setReportDenNgay(v as Dayjs)} 
          />
        </Form.Item>
        <Form.Item label={<Text strong>Trạng thái</Text>}>
          <Select 
            style={{ width: 160 }} 
            value={reportMaTT} 
            onChange={setReportMaTT}
          >
            <Option value={0}>— Tất cả —</Option>
            {dmTrangThai.map(t => <Option key={t.maTT} value={t.maTT}>{t.tenTT}</Option>)}
          </Select>
        </Form.Item>
        <Form.Item>
          <Space>
            <Button 
              type="primary" 
              icon={<SearchOutlined />} 
              onClick={handleSearchReport}
              loading={reportLoading}
            >
              Xem báo cáo
            </Button>
            <Button 
              icon={<FileExcelOutlined />} 
              onClick={handleExportExcel}
              disabled={reportData.length === 0}
              style={{ backgroundColor: '#1d713c', color: '#fff', border: 'none' }}
            >
              Xuất Excel
            </Button>
          </Space>
        </Form.Item>
      </Form>

      <Table<CskhReportDto>
        rowKey="idPhieu"
        size="small"
        loading={reportLoading}
        dataSource={reportData}
        columns={[
          { title: 'Ngày gọi', dataIndex: 'ngayGoi', key: 'ngayGoi', width: 140, render: v => dayjs(v).format('HH:mm DD/MM/YYYY') },
          { title: 'Họ tên', dataIndex: 'hoTen', key: 'hoTen', width: 180, render: (v, r) => (
            <Space direction="vertical" size={0}>
              <Text strong>{v}</Text>
              <Text type="secondary" style={{ fontSize: 11 }}>{r.maBN}</Text>
            </Space>
          )},
          { title: 'SĐT', dataIndex: 'dienThoai', key: 'dienThoai', width: 110 },
          { title: 'Cách LH', dataIndex: 'tenCLH', key: 'tenCLH', width: 120 },
          { title: 'Trạng thái', dataIndex: 'tenTT', key: 'tenTT', width: 120, render: (v, r) => (
            <Tag color={r.maTT === 4 ? 'green' : r.maTT === 1 ? 'red' : 'blue'}>{v}</Tag>
          )},
          { title: 'Đánh giá', dataIndex: 'tenLoaiDG', key: 'tenLoaiDG', width: 120 },
          { title: 'Ghi chú', dataIndex: 'ghiChu', key: 'ghiChu', ellipsis: true },
          { title: 'NV thực hiện', dataIndex: 'tenNV', key: 'tenNV', width: 150 },
        ]}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: total => `Tổng cộng: ${total} phiếu`
        }}
      />
    </Card>
  );

  return (
    <div style={{ padding: isLgUp ? 24 : 12 }}>
      <Tabs
        activeKey={mainActiveTab}
        onChange={setMainActiveTab}
        items={[
          {
            key: 'cskh',
            label: <span><HistoryOutlined />Nhật ký & Tra cứu</span>,
            children: renderCskhTab()
          },
          {
            key: 'report',
            label: <span><BarChartOutlined />Báo cáo & Xuất Excel</span>,
            children: renderReportTab()
          }
        ]}
      />

      {/* Modal chi tiết bệnh nhân */}
      <Modal
        title={
          <Space>
            <Text strong style={{ fontSize: 16 }}>
              👤 {detailData?.hoTen || 'Thông tin bệnh nhân'}
            </Text>
            {detailData?.maKCB && <Tag color="blue">Mã KCB: {detailData.maKCB}</Tag>}
            {detailData?.maBN && (
              <Tag color="purple">
                Mã BN: {detailData.maBN}
                {(() => {
                  const s = detailData?.maBN ? summaryMap.get(detailData.maBN) : undefined;
                  if (!s || s.soPhieu === 0) return '';
                  return ` (${s.soPhieu} phiếu)`;
                })()}
              </Tag>
            )}
          </Space>
        }
        open={detailModalOpen}
        onCancel={() => setDetailModalOpen(false)}
        footer={[
          <Button
            key="taophieu"
            type="primary"
            icon={<PlusOutlined />}
            disabled={!detailData}
            onClick={handleMoTaoPhieu}
          >
            Tạo phiếu CSKH
          </Button>,
          <Button key="close" onClick={() => setDetailModalOpen(false)}>
            Đóng
          </Button>
        ]}
        width={isMobile ? '95%' : 900}
        style={{ maxWidth: 900 }}
        destroyOnClose
      >
        <Spin spinning={detailLoading}>
          {detailData && (
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              size="small"
              items={[
                {
                  key: 'info',
                  label: '📋 Thông tin bệnh án',
                  children: (
                    <>
                      <Descriptions title="Thông tin cơ bản" bordered column={2} size="small">
                        <Descriptions.Item label="Mã KCB" span={1}>
                          <Text strong copyable>{detailData.maKCB}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Mã BN" span={1}>
                          <Text copyable>{detailData.maBN}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Họ tên" span={1}>
                          <Text strong>{detailData.hoTen}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Năm sinh / Tuổi" span={1}>
                          {detailData.namSinh} ({detailData.tuoi} tuổi)
                        </Descriptions.Item>
                        <Descriptions.Item label="Giới tính" span={1}>
                          {detailData.tenPhai}
                        </Descriptions.Item>
                        <Descriptions.Item label="Ngày đăng ký" span={1}>
                          {detailData.ngayDK ? dayjs(detailData.ngayDK).format('DD/MM/YYYY HH:mm') : '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Số điện thoại" span={1}>
                          <Text strong>{detailData.dienThoai || '—'}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Số CMND/CCCD" span={1}>
                          {detailData.soCMND || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Ngày cấp" span={1}>
                          {detailData.ngayCapCMND ? dayjs(detailData.ngayCapCMND).format('DD/MM/YYYY') : '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Số BHXH" span={1}>
                          {detailData.soBHXH || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Địa chỉ" span={2}>
                          {detailData.diaChi || '—'}
                        </Descriptions.Item>
                      </Descriptions>

                      <Divider />

                      <Descriptions title="Thông tin khám chữa bệnh" bordered column={2} size="small">
                        <Descriptions.Item label="Khoa khám" span={1}>
                          {detailData.tenKK || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Phòng" span={1}>
                          {detailData.tenPhong || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Đối tượng" span={1}>
                          {detailData.tenDoiTuong || '—'}
                        </Descriptions.Item>
                        <Descriptions.Item label="Đang điều trị" span={1}>
                          <Tag color={detailData.tenDangDieuTri ? 'green' : 'default'}>
                            {detailData.tenDangDieuTri || '—'}
                          </Tag>
                        </Descriptions.Item>
                      </Descriptions>

                      <Divider />

                      <Descriptions title="Chẩn đoán" bordered column={1} size="small">
                        <Descriptions.Item label="Chẩn đoán chính">
                          <Text type="danger">
                            {detailData.chanDoanChinh
                              ? detailData.chanDoanChinh.split(';').map((cd, i) => (
                                  <div key={i} style={{ marginBottom: 4 }}>• {cd.trim()}</div>
                                ))
                              : '—'}
                          </Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Chẩn đoán kèm theo">
                          <Text type="warning">
                            {detailData.chanDoanKemTheo
                              ? detailData.chanDoanKemTheo.split(';').map((cd, i) => (
                                  <div key={i} style={{ marginBottom: 4 }}>• {cd.trim()}</div>
                                ))
                              : '—'}
                          </Text>
                        </Descriptions.Item>
                      </Descriptions>
                    </>
                  )
                },
                {
                  key: 'phieu',
                  label: '📞 Lịch sử CSKH',
                  children: (
                    <Spin spinning={loadingPhieu}>
                      <Card
                        size="small"
                        title={`Danh sách phiếu (${danhSachPhieu.length})`}
                        extra={
                          <Button
                            size="small"
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleMoTaoPhieu}
                          >
                            Tạo mới
                          </Button>
                        }
                        style={{ marginTop: 8 }}
                      >
                        {danhSachPhieu.length === 0 ? (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Chưa có phiếu CSKH nào cho bệnh nhân này. Click "Tạo mới" để tạo phiếu đầu tiên.
                          </Text>
                        ) : (
                          danhSachPhieu.map(p => (
                            <Card
                              key={p.idPhieu}
                              size="small"
                              type="inner"
                              style={{ marginBottom: 8 }}
                              title={
                                <Space size={6}>
                                  <Text strong style={{ fontSize: 12 }}>
                                    #{p.idPhieu}
                                  </Text>
                                  <Tag color="blue">{p.tenCLH || `CLH#${p.maCLH}`}</Tag>
                                  <Tag
                                    color={
                                      p.maTT === 4
                                        ? 'green'
                                        : p.maTT === 3
                                        ? 'default'
                                        : p.maTT === 2
                                        ? 'orange'
                                        : 'red'
                                    }
                                  >
                                    {p.tenTT || `TT#${p.maTT}`}
                                  </Tag>
                                  {p.tenLoaiDG && (
                                    <Tag color={p.maLDG === 1 ? 'green' : p.maLDG === 3 ? 'red' : 'gold'}>
                                      {p.tenLoaiDG}
                                    </Tag>
                                  )}
                                  <Text type="secondary" style={{ fontSize: 11 }}>
                                    {dayjs(p.ngayGoi).format('HH:mm DD/MM/YYYY')}
                                  </Text>
                                </Space>
                              }
                            >
                              <Descriptions column={2} size="small" bordered={false}>
                                <Descriptions.Item label="Mã KCB" span={1}>
                                  {p.maKCB}
                                </Descriptions.Item>
                                <Descriptions.Item label="NV thực hiện" span={1}>
                                  <Text strong>{p.tenNV || p.maNV}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Ghi chú" span={2}>
                                  {p.ghiChu ? (
                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                      {p.ghiChu}
                                    </Text>
                                  ) : (
                                    <Text type="secondary" style={{ fontStyle: 'italic' }}>
                                      (không ghi chú)
                                    </Text>
                                  )}
                                </Descriptions.Item>
                              </Descriptions>
                            </Card>
                          ))
                        )}
                      </Card>
                    </Spin>
                  )
                },
                {
                  key: 'dichvu',
                  label: '💊 Dịch vụ đã sử dụng',
                  children: (
                    <Spin spinning={loadingDichVuSuDung}>
                      <Card
                        size="small"
                        title={`Danh sách dịch vụ (${dsDichVuSuDung.length})`}
                        style={{ marginTop: 8 }}
                      >
                        {dsDichVuSuDung.length === 0 ? (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Chưa có dữ liệu dịch vụ cho mã KCB này.
                          </Text>
                        ) : (
                          <Table<CskhDichVuSuDungDto>
                            rowKey="maTTCT"
                            size="small"
                            dataSource={dsDichVuSuDung}
                            pagination={{ pageSize: 15, showSizeChanger: true, showTotal: (t) => `Tổng ${t} dịch vụ` }}
                            scroll={{ x: 900, y: 380 }}
                            columns={[
                              {
                                title: 'Nhóm DV',
                                dataIndex: 'tenLoaiDichVu',
                                key: 'tenLoaiDichVu',
                                width: 150,
                                render: (v, r) => <Tag color="blue">{v}</Tag>
                              },
                              {
                                title: 'Mã DV',
                                dataIndex: 'maView',
                                key: 'maView',
                                width: 110,
                                render: (v) => <Text copyable>{v}</Text>
                              },
                              {
                                title: 'Tên dịch vụ',
                                dataIndex: 'tenDichVu',
                                key: 'tenDichVu',
                                ellipsis: true
                              },
                              {
                                title: 'SL',
                                dataIndex: 'soLuong',
                                key: 'soLuong',
                                width: 65,
                                align: 'right'
                              },
                              {
                                title: 'Thành tiền',
                                dataIndex: 'thanhTien',
                                key: 'thanhTien',
                                width: 120,
                                align: 'right',
                                render: (v) => Number(v ?? 0).toLocaleString('vi-VN')
                              },
                              {
                                title: 'TT BHYT',
                                dataIndex: 'thanhTienBH',
                                key: 'thanhTienBH',
                                width: 110,
                                align: 'right',
                                render: (v) => Number(v ?? 0).toLocaleString('vi-VN')
                              },
                              {
                                title: 'BNCCT',
                                dataIndex: 'tienBNCCT',
                                key: 'tienBNCCT',
                                width: 100,
                                align: 'right',
                                render: (v) => Number(v ?? 0).toLocaleString('vi-VN')
                              },
                              {
                                title: 'Tiền BN trả',
                                dataIndex: 'tienBN',
                                key: 'tienBN',
                                width: 120,
                                align: 'right',
                                render: (v) => <Text strong>{Number(v ?? 0).toLocaleString('vi-VN')}</Text>
                              }
                            ]}
                          />
                        )}
                      </Card>
                    </Spin>
                  )
                }
              ]}
            />
          )}
        </Spin>
      </Modal>

      {/* Modal Tạo phiếu CSKH */}
      <Modal
        title="➕ Tạo phiếu Chăm sóc khách hàng"
        open={taoPhieuModalOpen}
        confirmLoading={taoPhieuLoading}
        onCancel={() => setTaoPhieuModalOpen(false)}
        onOk={handleSubmitTaoPhieu}
        okText="Lưu phiếu"
        cancelText="Huỷ"
        width={isMobile ? '94%' : 640}
        style={{ maxWidth: 640 }}
        destroyOnClose
      >
        <Form
          form={taoPhieuForm}
          layout="vertical"
          disabled={taoPhieuLoading}
          style={{ marginTop: 8 }}
        >
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="maCLH"
                label="Cách liên hệ"
                rules={[{ required: true, message: 'Chọn cách liên hệ' },
                        { validator: (_, v) => v > 0 ? Promise.resolve() : Promise.reject('Chọn cách liên hệ') }]}
              >
                <Select placeholder="Chọn...">
                  {dmCachLienHe.map(item => (
                    <Option key={item.maCLH} value={item.maCLH}>{item.tenCLH}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="maTT"
                label="Trạng thái"
                rules={[{ required: true, message: 'Chọn trạng thái' },
                        { validator: (_, v) => v > 0 ? Promise.resolve() : Promise.reject('Chọn trạng thái') }]}
              >
                <Select
                  placeholder="Chọn..."
                  onChange={(val: number) => {
                    if (val !== 4) {
                      taoPhieuForm.setFieldsValue({ maLDG: 4 });
                    } else {
                      // Chuyển sang trạng thái "Bình thường" (4): xóa lựa chọn cũ cho user chọn lại
                      taoPhieuForm.setFieldsValue({ maLDG: null });
                    }
                  }}
                >
                  {dmTrangThai.map(item => (
                    <Option key={item.maTT} value={item.maTT}>{item.tenTT}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="maLDG" label="Đánh giá (tùy chọn)">
                <Select
                  placeholder="(không đánh giá)"
                  allowClear
                  disabled={selectedMaTT !== 4}
                >
                  {dmLoaiDanhGia.map(item => (
                    <Option key={item.maLDG} value={item.maLDG}>{item.tenLoaiDG}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="ngayGoi"
                label="Thời gian gọi"
                rules={[{ required: true, message: 'Chọn thời gian' }]}
              >
                <DatePicker
                  showTime
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY HH:mm"
                  disabledDate={c => !!c && c.isAfter(dayjs().endOf('day'))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="ghiChu" label="Ghi chú">
            <TextArea
              rows={4}
              placeholder="Nội dung trao đổi, phản hồi của khách hàng, yêu cầu thêm..."
              showCount
              maxLength={2000}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal Danh mục dịch vụ */}
      <Modal
        title={
          <Space>
            <BookOutlined />
            <Text strong style={{ fontSize: 16 }}>Danh mục giá dịch vụ</Text>
          </Space>
        }
        open={dichVuModalOpen}
        onCancel={() => setDichVuModalOpen(false)}
        footer={[
          <Button key="close" onClick={() => setDichVuModalOpen(false)}>Đóng</Button>
        ]}
        width={isMobile ? '95%' : 1000}
        style={{ maxWidth: 1000 }}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <Input.Search
            placeholder="Tìm theo mã hoặc tên dịch vụ..."
            allowClear
            enterButton="Tìm dịch vụ"
            loading={dichVuLoading}
            onSearch={handleSearchDichVu}
            onChange={e => setDichVuSearch(e.target.value)}
            value={dichVuSearch}
          />
        </div>
        <Table<CskhDichVuDto>
          rowKey="maView"
          size="small"
          loading={dichVuLoading}
          dataSource={dichVuData}
          columns={[
            { title: 'Mã dịch vụ', dataIndex: 'maView', key: 'maView', width: 120 },
            { title: 'Tên dịch vụ', dataIndex: 'tenDichVu', key: 'tenDichVu' },
            { 
              title: 'Giá BHYT', 
              dataIndex: 'giaBHYT', 
              key: 'giaBHYT', 
              width: 120, 
              align: 'right',
              render: (v) => v ? v.toLocaleString() : '—'
            },
            { 
              title: 'Giá Viện phí', 
              dataIndex: 'giaVienPhi', 
              key: 'giaVienPhi', 
              width: 120, 
              align: 'right',
              render: (v) => v ? v.toLocaleString() : '—'
            },
          ]}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Tổng: ${total} dịch vụ`
          }}
          scroll={{ y: 400 }}
        />
      </Modal>
    </div>
  );
};

export default CskhTab;
